import { useRef, useEffect } from 'react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';

import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import type {
  DialogType,
  DialogItemProps,
} from '../../contexts/DialogContext';
import { ROOT_ELEMENT_SELECTOR } from '../../consts';
import { useDialogContext } from '../../contexts/DialogContext';

//  Map a DialogType to the dialog component to use.
const dialogMap: Record<DialogType, (props: DialogItemProps<any>) => ReactElement> = {
  alert: AlertDialog,
  confirm: ConfirmDialog,
};

/**
 * DialogManager will render any dialogs in the DialogContext.
 * Instead of rendering them as direct descendants in the DOM,
 * DialogManager creates a Portal and appends the root element (#dashboard-main).
 */
const DialogManager = () => {
  const { state } = useDialogContext();
  const rootElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    rootElement.current = document.querySelector(ROOT_ELEMENT_SELECTOR);
  }, []);

  return state.map(({ type, ...dialogProps }) => {
    //  This is to appease TS/avoid a React error if there is no root element.
    if (!rootElement.current) {
      return null;
    }

    const Component = dialogMap[type];
    return createPortal(
      (
        <Component
          //  eslint-disable-next-line react/jsx-props-no-spreading
          {...dialogProps}
        />
      ),
      rootElement.current,
    );
  });
};

export default DialogManager;
